/** Catégories de dépense proposées par l'extraction IA. */
export const RECEIPT_CATEGORIES = [
  'Alimentation',
  'Transport',
  'Hébergement',
  'Bureautique',
  'Restauration',
  'Autre',
] as const;

export type ReceiptCategory = (typeof RECEIPT_CATEGORIES)[number];

/** Type de document détecté. */
export const DOCUMENT_TYPES = ['receipt', 'invoice'] as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const EXTRACTION_FIELDS = [
  'merchant',
  'date',
  'total',
  'vat',
  'currency',
  'category',
  'documentType',
] as const;

export type ReceiptExtractionField = (typeof EXTRACTION_FIELDS)[number];

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  receipt: 'Ticket',
  invoice: 'Facture',
};

/** Données extraites du document (par l'IA, réelles ou simulées). */
export interface ReceiptExtraction {
  merchant: string;
  /** Date au format ISO (yyyy-MM-dd). */
  date: string;
  /** Montant TTC. */
  total: number;
  /** Montant de TVA. */
  vat: number;
  /** Code devise ISO 4217. */
  currency: string;
  category: ReceiptCategory;
  documentType: DocumentType;
  /** Niveau de confiance de l'extraction, entre 0 et 1. */
  confidence: number;
  /** Lignes reconnues sur le document, conservées sans modifier le formulaire existant. */
  items?: ReceiptItem[];
  /** Champs que l'OCR n'a pas reconnus et que l'utilisateur doit renseigner/vérifier. */
  unrecognizedFields?: ReceiptExtractionField[];
}

export interface ReceiptItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

/** Statut de traitement d'un ticket, dérivé de sa confiance à la validation. */
export type ReceiptStatus = 'validated' | 'needs_review';

/** Déduit le statut d'un ticket à partir de son niveau de confiance. */
export function statusFromConfidence(confidence: number): ReceiptStatus {
  return confidence >= 0.85 ? 'validated' : 'needs_review';
}

export const RECEIPT_STATUS_LABELS: Record<ReceiptStatus, string> = {
  validated: 'Validé',
  needs_review: 'À revoir',
};

/** Un ticket/facture validé et persisté. */
export interface Receipt extends ReceiptExtraction {
  id: string;
  /** Nom du fichier d'origine. */
  fileName: string;
  /** MIME du fichier d'origine (image/* ou application/pdf). */
  fileType: string;
  /**
   * URL objet locale d'aperçu — présente uniquement juste après l'upload
   * (non persistée côté serveur).
   */
  previewUrl?: string;
  /** Statut de traitement, dérivé de la confiance par le serveur. */
  status?: ReceiptStatus;
  /** Date de validation (ISO 8601). */
  createdAt: string;
}
